import React, { useEffect, useState } from "react";
import { SME_ASSESSMENTS, GATE_TONE, type AssessmentDef } from "../api/sdhAPI_v2";

/**
 * DecidePopoverV2 — the SME's whole decision in one place: gate, reason, comment, done.
 *
 * Differences from v1, all subtractive:
 *  - it SAVES on Confirm. v1 collected decisions into a tray and required a second "Save decisions"
 *    step, which meant two mental models for one action.
 *  - the reason list only appears after a gate is chosen, so she never sees eight categories at once.
 *  - a comment is required on every decision (unchanged) because it is what the agent reads.
 */
const GATES: Array<{ key: "APPROVED" | "CONDITIONAL" | "PENDING" | "REJECTED"; label: string; hint: string }> = [
  { key: "APPROVED",    label: "Approve",     hint: "Correct as it stands" },
  { key: "CONDITIONAL", label: "Conditional", hint: "Right idea — something must change first" },
  { key: "PENDING",     label: "Pending",     hint: "Can't decide without the business" },
  { key: "REJECTED",    label: "Reject",      hint: "Should not be enforced" },
];

export default function DecidePopoverV2(
  { ruleName, current, defs, busy, onCancel, onConfirm }:
  { ruleName: string; current?: { assessment?: string; comment?: string };
    defs?: AssessmentDef[]; busy?: boolean;
    onCancel: () => void;
    onConfirm: (assessment: string, comment: string) => void }
) {
  const all = defs && defs.length ? defs : SME_ASSESSMENTS;
  const [gate, setGate] = useState<string>(
    current?.assessment ? (all.find((a) => a.value === current.assessment)?.gate as string) || "" : "");
  const [assessment, setAssessment] = useState<string>(current?.assessment || "");
  const [comment, setComment] = useState<string>(current?.comment || "");

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onCancel]);

  const options = all.filter((a) => a.gate === gate);
  // one reason under a gate → choose it silently; she should not pick from a list of one
  useEffect(() => { if (options.length === 1) setAssessment(options[0].value); }, [gate]); // eslint-disable-line
  const ready = !!assessment && comment.trim().length >= 3;
  const hint = gate === "APPROVED" ? "Why is this correct? (recorded with your approval)"
    : gate === "CONDITIONAL" ? "What must change before this can be enforced?"
    : gate === "PENDING" ? "What needs confirming, and by whom?"
    : "Why should this not be enforced?";

  return (
    <>
      <div className="d2-scrim" onClick={onCancel} />
      <div className="d2" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <style>{CSS}</style>
        <header><span className="t">{ruleName}</span><button className="x" onClick={onCancel}>×</button></header>

        <div className="d2-gates">
          {GATES.map((g) => (
            <button key={g.key} className={"g" + (gate === g.key ? " on" : "")}
              style={{ ["--t" as any]: GATE_TONE[g.key] }}
              onClick={() => { setGate(g.key); setAssessment(""); }}>
              <b>{g.label}</b><em>{g.hint}</em>
            </button>
          ))}
        </div>

        {gate && options.length > 1 && (
          <div className="d2-reasons">
            {options.map((o) => (
              <label key={o.value} className={"r" + (assessment === o.value ? " on" : "")}>
                <input type="radio" name="v2reason" checked={assessment === o.value}
                  onChange={() => setAssessment(o.value)} />
                <span>{o.label.replace(/^[^—]+—\s*/, "")}
                  {o.condition && <em> — clears when {o.condition}</em>}</span>
              </label>
            ))}
          </div>
        )}

        {gate && (
          <div className="d2-cmt">
            <textarea rows={3} placeholder={hint} value={comment}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)} />
            {!ready && <div className="warn">{!assessment ? "Pick a reason." : "A short comment is required."}</div>}
          </div>
        )}

        <footer>
          <button className="btn ghost" onClick={onCancel}>Cancel</button>
          <button className="btn go" disabled={!ready || busy}
            style={gate ? { background: GATE_TONE[gate as keyof typeof GATE_TONE] } : undefined}
            onClick={() => onConfirm(assessment, comment.trim())}>
            {busy ? "Saving…" : "Save decision"}
          </button>
        </footer>
      </div>
    </>
  );
}

const CSS = `
.d2-scrim{position:fixed;inset:0;z-index:80;background:rgba(14,36,54,.3)}
.d2{position:fixed;z-index:81;top:50%;left:50%;transform:translate(-50%,-50%);width:min(560px,94vw);
 background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(0,0,0,.26);overflow:hidden;
 font-family:system-ui,-apple-system,sans-serif;color:#0E2436}
.d2 header{display:flex;align-items:center;gap:10px;padding:13px 16px;background:#0e2436;color:#fff}
.d2 header .t{font-size:13.5px;font-weight:600;flex:1}
.d2 header .x{all:unset;cursor:pointer;font-size:22px;color:#cdd8ea}
.d2-gates{display:grid;grid-template-columns:repeat(2,1fr);gap:8px;padding:14px 16px 6px}
.d2-gates .g{all:unset;cursor:pointer;padding:10px 12px;border-radius:10px;background:#f4f7fa;border:1px solid transparent}
.d2-gates .g:hover{background:#eaf0f5}
.d2-gates .g.on{background:color-mix(in srgb,var(--t) 12%,#fff);border-color:var(--t)}
.d2-gates .g b{display:block;font-size:13.5px;color:#0E2436}
.d2-gates .g.on b{color:var(--t)}
.d2-gates .g em{font-style:normal;font-size:11.5px;color:#6b7c8d}
.d2-reasons{padding:6px 16px 0}
.d2-reasons .r{display:flex;gap:9px;align-items:flex-start;padding:7px 9px;border-radius:8px;cursor:pointer;font-size:13px}
.d2-reasons .r:hover{background:#f6f8fa}.d2-reasons .r.on{background:#eef2f6;font-weight:600}
.d2-reasons em{font-style:normal;color:#6b7c8d;font-weight:400;font-size:11.5px}
.d2-cmt{padding:10px 16px 4px}
.d2-cmt textarea{width:100%;box-sizing:border-box;font:inherit;font-size:13px;padding:9px 11px;
 border:1px solid #e3e8ee;border-radius:9px;resize:vertical}
.d2-cmt textarea:focus{outline:none;border-color:#0e7c7b;box-shadow:0 0 0 2px #d9ecea}
.d2-cmt .warn{font-size:11.5px;color:#a5570f;margin-top:5px}
.d2 footer{display:flex;justify-content:flex-end;gap:9px;padding:12px 16px;border-top:1px solid #e3e8ee;background:#f8fafb}
.btn{all:unset;cursor:pointer;padding:8px 16px;border-radius:9px;font-size:13px;font-weight:600}
.btn.ghost{background:#eef1f5;color:#42586e}.btn.go{background:#0e7c7b;color:#fff}
.btn[disabled]{opacity:.45;cursor:not-allowed}
`;
